/**
 * Danger Zone Tab
 * Archive + Delete actions wired to the same mutations and confirm flow
 * as the projects list. Delete is only available for archived projects
 * (mirrors backend lifecycle rules).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useRole } from '@/contexts/RoleContext';
import { useProjectLifecycleMutations } from '@/hooks/useProjectLifecycle';
import { ConfirmDialog } from '@/components/modals';
import { CONTROL_HEIGHT } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

interface DangerZoneTabProps {
  projectId: string;
  projectName: string;
  status: 'active' | 'paused' | 'archived';
  onArchived: () => void;
}

type PendingAction = 'archive' | 'delete' | null;

export function DangerZoneTab({
  projectId,
  projectName,
  status,
  onArchived,
}: DangerZoneTabProps) {
  const { t } = useTranslation(['settings', 'projects', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedOrgId, canEditProject, canDeleteProject } = useRole();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const isArchived = status === 'archived';
  const allowArchive =
    !isArchived && !!selectedOrgId && canEditProject(projectId, selectedOrgId);
  const allowDelete =
    isArchived && !!selectedOrgId && canDeleteProject(projectId, selectedOrgId);

  // Same mutations/toasts as the projects list — only the side effects differ.
  const { archiveMutation, deleteMutation } = useProjectLifecycleMutations({
    onArchiveSuccess: () => {
      setPendingAction(null);
      onArchived();
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onDeleteSuccess: () => {
      setPendingAction(null);
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void navigate({ to: '/projects' });
    },
  });

  return (
    <div className="space-y-6" data-testid="danger-zone-tab">
      <div className="border-t-2 border-danger pt-4">
        <div className="flex flex-col gap-3">
          {/* Archive */}
          <div className="flex items-center gap-3 p-3 border border-border rounded-md">
            <div className="flex-1">
              <div className="text-sm font-medium text-fg">
                {t('dangerZone.archiveProject', 'Archive Project')}
              </div>
              <div className="text-xs text-fg-muted">
                {isArchived
                  ? t('dangerZone.alreadyArchived', 'This project is archived.')
                  : !allowArchive
                    ? t(
                        'dangerZone.archivePermissionRequired',
                        "You don't have permission to archive this project."
                      )
                    : t(
                        'dangerZone.archiveDescription',
                        'Disable processing. Records preserved.'
                      )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPendingAction('archive')}
              disabled={!allowArchive || archiveMutation.isPending}
              data-testid="danger-zone-archive"
              className={cn(
                CONTROL_HEIGHT.md,
                'whitespace-nowrap px-3 text-sm rounded border border-warning text-warning-fg hover:bg-warning-dim transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              {t('dangerZone.archive', 'Archive')}
            </button>
          </div>

          {/* Delete */}
          <div className="flex items-center gap-3 p-3 border border-danger rounded-md">
            <div className="flex-1">
              <div className="text-sm font-medium text-fg">
                {t('dangerZone.deleteProject', 'Delete Project')}
              </div>
              <div className="text-xs text-fg-muted">
                {allowDelete
                  ? t(
                      'dangerZone.deleteDescription',
                      'Permanently delete project and all data. Cannot be undone.'
                    )
                  : !isArchived
                    ? t(
                        'dangerZone.deleteRequiresArchive',
                        'Archive the project first. Only archived projects can be deleted.'
                      )
                    : t(
                        'dangerZone.deletePermissionRequired',
                        "You don't have permission to delete this project."
                      )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPendingAction('delete')}
              disabled={!allowDelete || deleteMutation.isPending}
              data-testid="danger-zone-delete"
              className={cn(
                CONTROL_HEIGHT.md,
                'whitespace-nowrap px-3 text-sm rounded border border-danger text-danger-fg hover:bg-danger-dim transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              {t('dangerZone.delete', 'Delete')}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={() =>
          pendingAction === 'archive' && allowArchive
            ? archiveMutation.mutate(projectId)
            : pendingAction === 'delete' && allowDelete
              ? deleteMutation.mutate(projectId)
              : setPendingAction(null)
        }
        title={
          pendingAction === 'delete'
            ? t('projects:projects.deleteDialog.title')
            : t('projects:projects.archiveDialog.title')
        }
        message={
          pendingAction === 'delete'
            ? t('projects:projects.deleteDialog.message', { name: projectName })
            : t('projects:projects.archiveDialog.message', { name: projectName })
        }
        confirmText={
          pendingAction === 'delete'
            ? t('projects:projects.deleteDialog.confirm')
            : t('projects:projects.archiveDialog.confirm')
        }
        variant={pendingAction === 'delete' ? 'danger' : 'warning'}
      />
    </div>
  );
}
